import { useState, useEffect, useCallback, MouseEvent } from "react";
import { Flex, Text } from "rebass";
import styled from "styled-components";
import { HiClock } from "react-icons/hi";
import { TiCloudStorage } from "react-icons/ti";
import { useSetRecoilState } from "recoil";

import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";

const GRACE_PERIOD_END = new Date("2026-03-23T00:00:00Z").getTime();
const SEVENTY_TWO_HOURS_MS = 72 * 60 * 60 * 1000;

const useCountdown = (clipCreatedAt: string): string | null => {
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    const createdMs = new Date(clipCreatedAt).getTime();
    const deadline = Math.max(
      GRACE_PERIOD_END,
      createdMs + SEVENTY_TWO_HOURS_MS,
    );

    const update = () => {
      const now = Date.now();
      const remaining = deadline - now;

      if (remaining <= 0) {
        setTimeLeft("IMMINENT");
        return false;
      }

      const totalHours = Math.floor(remaining / (1000 * 60 * 60));
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else {
        setTimeLeft(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0",
          )}:${String(seconds).padStart(2, "0")}`,
        );
      }
      return true;
    };

    if (!update()) return;

    const interval = setInterval(() => {
      if (!update()) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [clipCreatedAt]);

  return timeLeft;
};

interface ExpiringPillProps {
  clipInternalId: string;
  clipCreatedAt: string;
  clipTitle: string;
  clipThumbnail: string;
  isOwner: boolean;
  variant: "card" | "sidebar";
}

export const ExpiringPill = ({
  clipInternalId,
  clipCreatedAt,
  clipTitle,
  clipThumbnail,
  isOwner,
  variant,
}: ExpiringPillProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const countdown = useCountdown(clipCreatedAt);

  const handleSave = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setAllstarModalState({
        data: {
          clipId: clipInternalId,
          clipTitle,
          clipThumbnail,
        },
        functions: {},
        isOpen: ModalType.SaveClipFlow,
      });

      analytics.track("Save Clip - Open Flow", {
        clipId: clipInternalId,
        source: variant === "card" ? "profile_card_pill" : "sidebar_pill",
      });
    },
    [clipInternalId, clipTitle, clipThumbnail, setAllstarModalState, variant],
  );

  const isImminent = countdown === "IMMINENT";

  if (!countdown) return null;

  const showSaveHint = isOwner && isHovering;

  if (variant === "card") {
    return (
      <CardPill
        onClick={isOwner ? handleSave : undefined}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        $clickable={isOwner}
        $hovering={showSaveHint}
      >
        {showSaveHint ? <TiCloudStorage size={13} /> : <HiClock size={13} />}
        <PillText>
          {showSaveHint
            ? "Save Clip"
            : isImminent
            ? "Expiring Imminently"
            : `Expires ${countdown}`}
        </PillText>
      </CardPill>
    );
  }

  return (
    <SidebarPill>
      <SidebarLeft>
        <HiClock size={15} />
        <SidebarText>
          {isImminent ? "Expiring Imminently" : `Expires in ${countdown}`}
        </SidebarText>
      </SidebarLeft>
      {isOwner && (
        <SaveButton onClick={handleSave}>
          <TiCloudStorage size={15} />
          <SaveLabel>Save</SaveLabel>
        </SaveButton>
      )}
    </SidebarPill>
  );
};

const CardPill = styled(Flex)<{
  $clickable?: boolean;
  $hovering?: boolean;
}>`
  align-items: center;
  backdrop-filter: blur(12px);
  background: ${({ theme, $hovering }) =>
    $hovering ? theme.colors.envy : "rgba(22, 35, 44, 0.85)"};
  border: 1px solid
    ${({ theme, $hovering }) =>
      $hovering ? "rgba(255, 255, 255, 0.3)" : theme.colors.navy50};
  border-radius: 999px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  gap: 5px;
  height: 28px;
  justify-content: center;
  left: 12px;
  padding: 0 10px;
  position: absolute;
  top: 12px;
  transition: all 0.2s ease;
  z-index: 5;

  @media (max-width: 480px) {
    height: 24px;
    left: 8px;
    padding: 0 8px;
    top: 8px;
  }
`;

const PillText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const SidebarPill = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50}40;
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
  justify-content: space-between;
  padding: 10px 16px;
  width: 100%;
`;

const SidebarLeft = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade300};
  gap: 6px;
`;

const SidebarText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 600;
`;

const SaveButton = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  height: 28px;
  justify-content: center;
  padding: 0 12px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envy};
  }
`;

const SaveLabel = styled(Text)`
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
`;
